import React, { useEffect, useState } from "react";
import { api, fmtMoney, formatErr } from "@/lib/api";
import { logError } from "@/lib/log";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, X, MapPin, Navigation } from "lucide-react";
import { toast } from "sonner";

const todayISO = () => new Date().toISOString().slice(0, 10);
const mapsUrl = (addr) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`;

/** Admin "New ticket" button + dialog. Owns all of its own form state. */
export default function TicketCreateDialog({ customers, settings, onCreated }) {
  const [open, setOpen] = useState(false);
  const [customer, setCustomer] = useState("");
  const [date, setDate] = useState(todayISO());
  const [notes, setNotes] = useState("");
  const [pools, setPools] = useState([]); // [{pool_id, pool_name, pool_address, tank_size, gallons}]
  const [fallbackGallons, setFallbackGallons] = useState(0); // if customer has no pools
  const [refillCarboys, setRefillCarboys] = useState(false);
  const [refillCarboysQty, setRefillCarboysQty] = useState(1);

  // Load customer's pools when a customer is selected
  useEffect(() => {
    let cancelled = false;
    const loadPools = async () => {
      if (!customer) {
        if (!cancelled) setPools([]);
        return;
      }
      try {
        const r = await api.get(`/customers/${customer}/pools`);
        if (cancelled) return;
        setPools(r.data.map((p) => ({
          pool_id: p.id, pool_name: p.name, pool_address: p.address,
          tank_size: p.tank_size || 0, gallons: p.tank_size || 0,
        })));
        // Preselect the carboy switch if the customer's profile says so.
        const cust = customers.find((c) => c.id === customer);
        if (cust && cust.refill_empty_jugs && !cancelled) {
          setRefillCarboys(true);
        }
      } catch (e) { logError("Pool load failed:", e); }
    };
    loadPools();
    return () => { cancelled = true; };
  }, [customer, customers]);

  const reset = () => {
    setCustomer(""); setDate(todayISO()); setNotes("");
    setPools([]); setFallbackGallons(0);
    setRefillCarboys(false); setRefillCarboysQty(1);
  };

  const previewSubtotal = () => {
    const price = Number(settings.price_per_gallon || 5);
    const poolTotal = pools.reduce((s, p) => s + (Number(p.gallons || 0) * price), 0);
    const fallback = pools.length === 0 ? Number(fallbackGallons || 0) * price : 0;
    return poolTotal + fallback;
  };

  const submit = async () => {
    if (!customer) return toast.error("Pick a customer");
    const hasGallons = pools.some((p) => Number(p.gallons || 0) > 0) || Number(fallbackGallons || 0) > 0;
    if (!hasGallons && !refillCarboys) return toast.error("Add gallons or enable carboy refill");
    try {
      await api.post("/tickets", {
        customer_id: customer,
        delivery_date: date,
        notes,
        pool_deliveries: pools.map(({ pool_id, gallons }) => ({ pool_id, gallons: Number(gallons || 0) })),
        gallons: Number(fallbackGallons || 0),
        items: [],
        products_only: false,
        refill_carboys: refillCarboys,
        refill_carboys_qty: refillCarboys ? Number(refillCarboysQty || 1) : 0,
      });
      toast.success("Open ticket created");
      setOpen(false); reset();
      onCreated && onCreated();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button className="rounded-sm h-10" data-testid="new-ticket-button"><Plus className="w-4 h-4 mr-2" /> New ticket</Button>
      </DialogTrigger>
      <DialogContent className="rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">New delivery ticket</DialogTitle>
          <DialogDescription>Tickets are created as OPEN. Close them once the driver confirms gallons — an invoice is auto-generated &amp; emailed.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Customer</Label>
              <Select value={customer} onValueChange={setCustomer}>
                <SelectTrigger className="rounded-sm" data-testid="ticket-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.business_name || c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Delivery date</Label>
              <Input type="date" className="rounded-sm" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
          </div>

          {/* Pools / chlorine delivery */}
          <div className="border border-border p-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="overline">Pool deliveries · {pools.length} pool{pools.length === 1 ? "" : "s"}</p>
            </div>
            {pools.length === 0 && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  {customer ? "No pools on this customer — enter gallons directly:" : "Pick a customer to load their pools."}
                </p>
                <div className="flex items-center gap-2">
                  <Label className="text-sm">Gallons</Label>
                  <Input type="number" step="0.1" className="rounded-sm w-32 mono-num" value={fallbackGallons}
                         onChange={(e) => setFallbackGallons(parseFloat(e.target.value || 0))} data-testid="ticket-fallback-gallons" />
                </div>
              </div>
            )}
            {pools.map((p, idx) => (
              <div key={p.pool_id} className="grid grid-cols-12 gap-2 items-center" data-testid={`create-pool-${idx}`}>
                <div className="col-span-4">
                  <div className="font-medium text-sm">{p.pool_name}</div>
                  <div className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3" /> {p.pool_address}</div>
                </div>
                <div className="col-span-3 text-xs text-muted-foreground">Tank: <span className="mono-num">{p.tank_size}</span> gal</div>
                <div className="col-span-3 flex items-center gap-1">
                  <Input type="number" step="0.1" min="0" className="rounded-sm mono-num text-right" value={p.gallons}
                         onChange={(e) => {
                           const v = parseFloat(e.target.value || 0);
                           setPools(pools.map((x, i) => i === idx ? { ...x, gallons: v } : x));
                         }} data-testid={`create-pool-gallons-${idx}`} />
                  <span className="text-xs text-muted-foreground">gal</span>
                </div>
                <a href={mapsUrl(p.pool_address)} target="_blank" rel="noopener noreferrer"
                   className="col-span-1 text-primary text-xs hover:underline flex items-center justify-center">
                  <Navigation className="w-4 h-4" />
                </a>
                <Button type="button" size="sm" variant="ghost"
                        className="col-span-1 rounded-sm h-8 w-8 p-0 text-destructive hover:text-destructive"
                        title="Remove this pool from the ticket"
                        onClick={() => setPools(pools.filter((_, i) => i !== idx))}
                        data-testid={`create-pool-remove-${idx}`}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>

          {/* Carboy (5-gallon jug swap) — the ONLY product allowed on chlorine tickets */}
          <div className="border border-border p-4 space-y-3" data-testid="ticket-carboy-block">
            <div className="flex items-start gap-3">
              <input type="checkbox" className="mt-1" checked={refillCarboys}
                     onChange={(e) => setRefillCarboys(e.target.checked)}
                     data-testid="ticket-refill-carboys-toggle" />
              <div className="flex-1">
                <p className="font-semibold text-sm">Refill empty 5-gallon jugs (Carboy)</p>
                <p className="text-xs text-muted-foreground">The driver will swap and refill these on-site. This is the only product type allowed on chlorine tickets — for anything else, use Orders &amp; Quotes.</p>
              </div>
              {refillCarboys && (
                <div className="flex items-center gap-2">
                  <Label className="text-xs">Qty</Label>
                  <Input type="number" min="1" step="1" className="rounded-sm w-20 mono-num"
                         value={refillCarboysQty}
                         onChange={(e) => setRefillCarboysQty(parseInt(e.target.value || 1))}
                         data-testid="ticket-refill-carboys-qty" />
                </div>
              )}
            </div>
          </div>

          <div><Label>Notes</Label><Textarea className="rounded-sm" value={notes} onChange={(e) => setNotes(e.target.value)} /></div>

          <div className="flex justify-between items-center text-sm pt-2 border-t border-border">
            <span className="overline">Subtotal preview</span>
            <span className="font-display text-2xl font-black tracking-tighter mono-num">{fmtMoney(previewSubtotal())}</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={submit} className="rounded-sm" data-testid="save-ticket-button">Create open ticket</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
